import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import styles from "./CountrySelect.module.css";
import { getCountryMeta } from "./countryHelpers";
import { ArrowLeft, Search } from "lucide-react";

const CountrySelect = ({
  value,
  onChange,
  options = [],
  directory,
  countriesLoading = false,
  countriesError = false,
  onRetryCountries,
  disabled,
  readOnly,
  iconComponent: IconComponent,
  className: _unusedClassName = "",
  name,
  onFocus,
  onBlur,
  onValidationChange,
  editableCallingCode = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [callingCodeDraft, setCallingCodeDraft] = useState(null);
  const callingCodeErrorId = useId();
  const [brokenFlags, setBrokenFlags] = useState({});
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 575px)').matches);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 575px)');
    const update = () => setIsMobile(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !isMobile) return undefined;
    const viewport = window.visualViewport;
    const update = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown || (viewport && Math.abs(viewport.scale - 1) >= 0.01)) return;
      dropdown.style.top = `${viewport?.offsetTop ?? 0}px`;
      dropdown.style.left = `${viewport?.offsetLeft ?? 0}px`;
      dropdown.style.width = `${viewport?.width ?? window.innerWidth}px`;
      dropdown.style.height = `${viewport?.height ?? window.innerHeight}px`;
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isMobile]);

  const isSelectorDisabled = disabled || readOnly;

  const countryOptions = useMemo(() => {
    const allowedCodes = new Set(options.filter((option) => !option.divider).map((option) => option.value));
    return directory?.countries.filter((country) => allowedCodes.has(country.code)) || [];
  }, [directory, options]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const label = options.find((option) => option.value === value)?.label;
    return getCountryMeta(value, label, directory?.byCode);
  }, [directory, options, value]);

  const callingCode = callingCodeDraft ?? (selectedOption?.dialCode ? `+${selectedOption.dialCode}` : "");
  const matchingCountry = countryOptions.find((option) =>
    option.code === value && `+${option.dialCode}` === callingCode
  ) || countryOptions.find((option) => `+${option.dialCode}` === callingCode);
  const callingCodeError = callingCodeDraft !== null && !matchingCountry
    ? "Enter a valid country calling code" : "";

  useEffect(() => {
    onValidationChange?.(callingCodeError);
  }, [callingCodeError, onValidationChange]);

  useEffect(() => {
    setCallingCodeDraft(null);
  }, [value]);

  const commitCallingCode = () => {
    if (!matchingCountry) return;
    setCallingCodeDraft(null);
    if (matchingCountry.code !== value) onChange(matchingCountry.code);
  };

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return countryOptions;

    const dialQuery = /^\+?\d+$/.test(normalizedQuery)
      ? normalizedQuery.replace(/^\+/, "")
      : null;
    const exactMatches = [];
    const prefixMatches = [];
    const partialMatches = [];

    for (const option of countryOptions) {
      if (
        !option.searchName.includes(normalizedQuery) &&
        !option.searchCode.includes(normalizedQuery) &&
        !`+${option.dialCode}`.includes(normalizedQuery)
      ) {
        continue;
      }

      if (dialQuery) {
        if (option.dialCode === dialQuery) {
          exactMatches.push(option);
        } else if (option.dialCode.startsWith(dialQuery)) {
          prefixMatches.push(option);
        } else {
          partialMatches.push(option);
        }
      } else if (option.searchName.startsWith(normalizedQuery)) {
        prefixMatches.push(option);
      } else {
        partialMatches.push(option);
      }
    }

    // Stable groups avoid sorting and preserve the original order within each rank.
    return [...exactMatches, ...prefixMatches, ...partialMatches];
  }, [countryOptions, query]);

  useEffect(() => {
    if (!isOpen || isMobile) return undefined;

    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) &&
          !dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isOpen, isMobile]);

  const handleToggle = () => {
    if (isSelectorDisabled) {
      return;
    }
    if (isOpen) {
      closeDropdown(true);
      return;
    }
    // Mount and focus within the tap handler so iOS can open the keyboard.
    flushSync(() => {
      setQuery("");
      setIsOpen(true);
    });
    searchRef.current?.focus({ preventScroll: true });
  };

  const closeDropdown = (restoreFocus = false) => {
    searchRef.current?.blur();
    flushSync(() => {
      setIsOpen(false);
      setQuery("");
    });
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const handleSelect = (nextValue) => {
    // Close before the phone library focuses the number field.
    closeDropdown();
    setCallingCodeDraft(null);
    onChange(nextValue);
  };

  const handleDropdownKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeDropdown(true);
      return;
    }
    if (!isMobile || event.key !== 'Tab') return;

    const controls = dropdownRef.current.querySelectorAll('button:not(:disabled), input');
    const first = controls[0];
    const last = controls[controls.length - 1];
    const target = event.shiftKey && document.activeElement === first ? last
      : !event.shiftKey && document.activeElement === last ? first : null;
    if (target) {
      event.preventDefault();
      target.focus();
    }
  };

  const renderFlag = (option) => {
    if (option.flagSrc && !brokenFlags[option.code]) {
      return (
        <img
          src={option.flagSrc}
          alt={option.code ? `${option.code} flag` : "Country flag"}
          className={styles.flag}
          width={22}
          height={16}
          loading="lazy"
          decoding="async"
          onError={() =>
            setBrokenFlags((previous) => ({
              ...previous,
              [option.code]: true,
            }))
          }
        />
      );
    }

    // Wait for the API before deciding that this country needs a fallback.
    if (countriesLoading) {
      return <span className={styles.flag} aria-hidden />;
    }

    if (IconComponent && option.code) {
      return (
        <span className={styles.flagFallback}>
          <IconComponent country={option.code} label={option.name} aria-hidden />
        </span>
      );
    }

    return (
      <span className={styles.flagPlaceholder} aria-hidden>
        {option.code ? option.code.slice(0, 2) : "--"}
      </span>
    );
  };

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      role={isMobile ? 'dialog' : undefined}
      aria-modal={isMobile ? true : undefined}
      aria-label="Choose country calling code"
      onKeyDown={handleDropdownKeyDown}
    >
      <div className={`${styles.searchHeader} space-x`}>
        <button
          type="button"
          aria-label="Close country selection"
          className={`${styles.closeList} btn`}
          onClick={() => closeDropdown(true)}
        >
          <ArrowLeft size={20} color="var(--Neutral900)" />
        </button>
        <label className={styles.formControl}>
          <span className={styles.searchIcon}>
            <Search size={20} color="var(--Neutral900)" />
          </span>
          <input
            ref={searchRef}
            type="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="done"
            onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }}
            aria-label="Search country"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search country"
            className={`${styles.searchInput} form-control`}
          />
        </label>
      </div>

      <ul className={styles.list} role="listbox" aria-label="Countries">
        {countriesLoading ? (
          <li className={styles.noResults} role="status">Loading countries…</li>
        ) : countriesError ? (
          <li className={styles.noResults} role="status">
            Unable to load countries.{' '}
            <button type="button" className="btn underline" onClick={() => onRetryCountries?.()}>
              Try again
            </button>
          </li>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value || "INTL"} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`.trim()}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className={styles.optionLeft}>
                    {renderFlag(option)}
                    <span className={styles.optionName}>{option.name}</span>
                  </span>
                  <span className={styles.optionDialCode}>
                    {option.dialCode ? `+${option.dialCode}` : ""}
                  </span>
                </button>
              </li>
            );
          })
        ) : (
          <li className={styles.noResults}>
            <Search size={28} strokeWidth={1.5} color="var(--Neutral400)" />
            <span>No countries found</span>
            <span className={styles.noResultsSub}>Try a different name or dial code</span>
          </li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className={`${styles.countrySelect} ${editableCallingCode ? styles.editableCallingCode : ''}`} ref={containerRef}>
      <input
        type="hidden"
        name={name}
        value={value || ""}
        className={_unusedClassName || undefined}
      />

      <button
        ref={triggerRef}
        type="button"
        className={styles.selectorButton}
        onClick={handleToggle}
        disabled={isSelectorDisabled}
        aria-label="Choose country calling code"
        aria-haspopup={isMobile ? "dialog" : "listbox"}
        aria-expanded={isOpen}
        onFocus={onFocus}
        onBlur={onBlur}
        {...rest}
      >
        <span className={styles.buttonContent}>
          {selectedOption ? renderFlag(selectedOption) : null}
          {!editableCallingCode && <span className={styles.selectedCode}>
            {selectedOption?.dialCode ? ` +${selectedOption.dialCode}` : ""}
          </span>}
        </span>
        <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ""}`} />
      </button>

      {editableCallingCode && <input
        className={styles.callingCodeInput}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        aria-label="Country calling code"
        aria-invalid={Boolean(callingCodeError)}
        aria-describedby={callingCodeError ? callingCodeErrorId : undefined}
        value={callingCode}
        style={{ width: `${Math.max(3, callingCode.length)}ch` }}
        disabled={disabled}
        readOnly={readOnly || countriesLoading}
        onFocus={(event) => {
          event.currentTarget.select();
          onFocus?.(event);
        }}
        onChange={(event) => {
          setCallingCodeDraft(`+${event.target.value.replace(/\D/g, "")}`);
        }}
        onBlur={(event) => {
          commitCallingCode();
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            commitCallingCode();
          }
          if (event.key === 'Escape') setCallingCodeDraft(null);
        }}
      />}
      {callingCodeError && (
        <span id={callingCodeErrorId} className={styles.callingCodeError}>
          {callingCodeError}
        </span>
      )}

      {isMobile && dropdown ? createPortal(dropdown, document.body) : dropdown}
    </div>
  );
};

export default CountrySelect;
